import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Grid,
  Typography,
  Button,
  TextField,
  Box,
  Card,
  Rating,
  CircularProgress,
} from "@mui/material";
import Header from "../../Layout/Header/header";
import ContractorImg from "../../images/ContractorImg.svg";
import LocationOn from "../../icon/Loaction.svg";
import GroupIcon from "../../icon/Group.svg";
import Footer from "../../Layout/Footer/footer";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_CONFIG } from "../../api/apiUrl";
import { AuthContext } from "../../context/authcontext";
import { ChevronLeft, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";

interface Contractor {
  id: string;
  name: string;
  speciality?: string;
  address: string;
  cities?: { name: string };
  projects?: number;
  rating?: number;
  companyLogo?: string;
  plan?: string;
  price?: string;
  pricingPlans?: any[];
}

const ContractorSearch = () => {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [params, setParams] = useState<{ page: number; searchTerm: string }>({
    page: 0,
    searchTerm: "",
  });
  const authContext = useContext(AuthContext);
  const user = authContext ? authContext.user : null;
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("id_token");

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const pageParam = parseInt(urlParams.get("page") || "1", 10) - 1;
    const searchParam = urlParams.get("search") || "";
    setParams({
      page: pageParam >= 0 ? pageParam : 0,
      searchTerm: searchParam,
    });
  }, [location.search]);

  useEffect(() => {
    const fetchContractors = async () => {
      setLoading(true);
      try {
        const baseUrl = API_CONFIG.COMPANY_PROFILES(params.page, rowsPerPage);
        const url = params.searchTerm
          ? `${baseUrl}&name.contains=${encodeURIComponent(params.searchTerm)}`
          : baseUrl;
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setContractors(response.data);
        const total =
          response.headers && response.headers["x-total-count"]
            ? parseInt(response.headers["x-total-count"], 10)
            : response.data.length;
        setTotalCount(total);
      } catch (error) {
        console.error("Error fetching contractors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContractors();
  }, [params.page, params.searchTerm, rowsPerPage, token]);

  useEffect(() => {
    const urlParams = new URLSearchParams();
    if (params.page > 0) urlParams.set("page", (params.page + 1).toString());
    if (params.searchTerm) urlParams.set("search", params.searchTerm);
    const newSearch = urlParams.toString();
    if (newSearch !== location.search) {
      navigate(
        { pathname: location.pathname, search: newSearch },
        { replace: true }
      );
    }
  }, [
    params.page,
    params.searchTerm,
    navigate,
    location.pathname,
    location.search,
  ]);

  const handleSearchChange = (value: string) => {
    setParams((prev) => ({ ...prev, searchTerm: value, page: 0 }));
  };

  const handleSearchButton = () => {
    setParams((prev) => ({ ...prev, page: 0 }));
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const maxVisibleButtons = 10;
  const paginationRange = useMemo(() => {
    const safePage = params.page;
    const range = [];
    const delta = Math.floor(maxVisibleButtons / 2);
    let start = Math.max(0, safePage - delta);
    let end = Math.min(totalPages - 1, start + maxVisibleButtons - 1);
    if (end - start + 1 < maxVisibleButtons) {
      start = Math.max(0, end - maxVisibleButtons + 1);
    }
    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  }, [params.page, totalPages]);

  const handleAssignedClick = async (contractor: Contractor) => {
    const booking = location.state?.booking || null;
    let plot = booking?.plot || null;
    let files = booking?.files || [];
    let services = plot?.services || [];
    let contractorWithPlans = { ...contractor };
    if (!plot || !plot.id) {
      toast.error("Plot not found. Please go back and select a valid plot.");
      return;
    }
    try {
      const token = localStorage.getItem("id_token");
      const response = await axios.get(
        API_CONFIG.COMPANY_PROFILE_RATES_BY_ID(contractor.id),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const plans: any[] = [];
      const processedTypeIds = new Set();
      response.data.forEach((item: any) => {
        if (item.rateCardTypes && item.companyRates) {
          const typeId = item.rateCardTypes.id;
          processedTypeIds.add(typeId);
          plans.push({
            name: item.rateCardTypes.name,
            price: item.companyRates.price || item.rateCardTypes.defaultPrice,
            isAvailable: true,
            companyRateId: item.companyRates.id,
            rateCardTypeId: typeId,
            features:
              item.rateCardItems?.map((feature: any) => ({
                id: feature.rateCardItems.id,
                name: feature.rateCardItems.name,
                isEnabled: true,
              })) || [],
            editing: false,
            showAllFeatures: false,
          });
        }
      });
      response.data.forEach((item: any) => {
        if (item.rateCardNotMapped && item.rateCardNotMapped.length > 0) {
          item.rateCardNotMapped.forEach((unmappedItem: any) => {
            const typeId = unmappedItem.rateCardTypes.id;
            if (processedTypeIds.has(typeId)) return;
            processedTypeIds.add(typeId);
            plans.push({
              name: unmappedItem.rateCardTypes.name,
              price: unmappedItem.rateCardTypes.defaultPrice,
              isAvailable: true,
              companyRateId: 0,
              rateCardTypeId: typeId,
              features: unmappedItem.rateCardItems.map((feature: any) => ({
                id: feature.rateCardItems.id,
                name: feature.rateCardItems.name,
                isEnabled: true,
              })),
              editing: false,
              showAllFeatures: false,
            });
          });
        }
      });
      contractorWithPlans.pricingPlans = plans;
    } catch (err) {
      contractorWithPlans.pricingPlans = [];
    }
    navigate(`/estimate?plotId=${plot.id}`,
      { state: { plot, files, services, contractor: contractorWithPlans, unselected: booking?.unselected || [] } }
    );
  };

  return (
    <>
      <Header />
      <Box sx={{ maxWidth: "2000px", margin: "0 auto" }}>
        <Box sx={styles.mainBox}>
          <Box sx={styles.headerBox}>
            <Grid container spacing={4} sx={styles.gridContainer}>
              <Grid item xs={12} md={7}>
                <Box sx={{ mt: 2 }}>
                  <Typography sx={styles.welcomeText}>
                    <Typography component="span" sx={styles.welcomeHighlight}>
                      Welcome
                    </Typography>
                    <span> {user?.firstName || "User"}</span>
                  </Typography>
                  <Typography sx={styles.subTitle}>
                    Let's Find Your Local Contractors
                  </Typography>
                  <Box sx={styles.searchBox}>
                    <TextField
                      fullWidth
                      sx={styles.textField}
                      placeholder="Search..."
                      value={params.searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                    <Button
                      sx={styles.searchButton}
                      onClick={handleSearchButton}
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  ...styles.imageContainer,
                  display: { xs: "none", md: "block" },
                }}
              >
                <img
                  src={ContractorImg}
                  alt="Contractor"
                  style={styles.image}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 6.4 } }}>
          <Box sx={{ my: { xs: 1, md: 3 } }}>
            {(loading || contractors.length > 0) && (
              <Typography
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "18px", sm: "24px", md: "30px" },
                }}
              >
                {totalCount === 1
                  ? "Showing Result: 1 Contractor Found"
                  : `Showing Results: ${totalCount} Contractors Found`}
              </Typography>
            )}
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <CircularProgress sx={{ color: "#89652A" }} />
              </Box>
            ) : contractors.length > 0 ? (
              contractors.map((contractor) => (
                <Card
                  key={contractor.id}
                  elevation={0}
                  sx={{
                    ...styles.contractorCard,
                    p: { xs: 1, sm: 1, md: 1 },
                    px: { xs: 1, sm: 2, md: 3 },
                    mb: { xs: 1, md: 1 },
                    minHeight: 'auto'
                  }}
                  onClick={e => {
                    const target = e.target as Element;
                    if (target && target.closest && target.closest('.assigned-link')) return;
                    navigate(`/contractors-profile/${contractor.id}`);
                  }}
                >
                  <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ display: "flex", alignItems: "center", padding: { xs: 0.5, sm: 1, md: 1 } }}>
                    <Grid item xs={12} md={4} sm={5} lg={5}>
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", padding: { xs: 0.5, sm: 1, md: 1 } }}>
                        {contractor.companyLogo && contractor.companyLogo !== "string" ? (
                          <Box
                            component="img"
                            src={contractor.companyLogo}
                            alt={contractor.name}
                            sx={{
                              width: { xs: 200, sm: 220, md: 240, lg: 260 },
                              height: { xs: 130, sm: 140, md: 150, lg: 160 },
                              borderRadius: 2,
                              objectFit: "cover",
                              background: "#eee",
                              display: "block"
                            }}
                          />
                        ) : (
                          <Box sx={{
                            width: { xs: 200, sm: 220, md: 240, lg: 260 },
                            height: { xs: 130, sm: 140, md: 150, lg: 160 },
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f9f9f9"
                          }}>
                            <Typography sx={{ textTransform: "capitalize", fontSize: "14px", color: "#666", textAlign: "center" }}>No images available</Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={8} sm={7} lg={7} sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", height: "100%", padding: { xs: 0.5, sm: 1, md: 1 } }}>
                      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 0.3 }}>
                        <Typography sx={{ fontWeight: 500, textAlign: "left", fontSize: { xs: "16px", sm: "18px", md: "20px" }, mb: 0.3 }}>{contractor.name}</Typography>
                        <Typography sx={{ fontWeight: 400, fontSize: { xs: "13px", sm: "14px", md: "15px" }, color: "#666", mb: 0.3 }}>{contractor.speciality || "Not specified"}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, mb: 0.3 }}>
                          <img src={LocationOn} alt="" style={{ width: "14px", height: "14px" }} />
                          <Typography sx={{ fontWeight: 400, fontSize: { xs: "13px", sm: "14px", md: "15px" }, color: "#555" }}>{contractor.address}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, mb: 0.5 }}>
                          <img src={GroupIcon} alt="" style={{ width: "14px", height: "14px" }} />
                          <Typography sx={{ fontWeight: 400, fontSize: { xs: "13px", sm: "14px", md: "15px" }, color: "#555" }}>{contractor.projects || 0} Projects</Typography>
                        </Box>
                        <Rating value={contractor.rating || 0} readOnly sx={{ mb: 0.5, color: "#89652A", "& .MuiRating-icon": { fontSize: { xs: "14px", sm: "16px", md: "18px" } } }} />
                        <Typography
                          sx={{
                            borderRadius: "50px",
                            textTransform: "capitalize",
                            width: "max-content",
                            border: "1px solid #89652A",
                            padding: { xs: "5px 12px", sm: "6px 14px", md: "6px 16px" },
                            fontSize: { xs: "12px", sm: "13px", md: "14px" },
                            fontWeight: 500,
                            cursor: 'pointer',
                            color: '#89652A'
                          }}
                          className="assigned-link"
                          onClick={async e => { e.stopPropagation(); await handleAssignedClick(contractor); }}
                        >
                          Assign
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              ))
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                }}
              >
                <img
                  src={
                    require("../../images/herosecImg/404 error with person looking for-rafiki 1.svg")
                      .default
                  }
                  alt="Not found"
                  style={{ maxWidth: 220, marginBottom: 24, opacity: 0.8 }}
                />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "#89652A", mb: 1 }}
                >
                  No contractors found
                </Typography>
                <Typography
                  sx={{
                    color: "#666",
                    fontSize: 16,
                    textAlign: "center",
                    maxWidth: 350,
                  }}
                >
                  Sorry, we couldn't find any contractors matching your search.{" "}
                  <br />
                  Try changing your search term or check back later.
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                mt: 3,
                mb: 3,
              }}
            >
              <Button
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#89652A",
                  "&:hover": { backgroundColor: "#B69D74" },
                  minWidth: "10px",
                }}
                onClick={() =>
                  setParams((prev) => ({ ...prev, page: prev.page - 1 }))
                }
                disabled={params.page === 0 || loading}
              >
                <ChevronLeft />
              </Button>
              {paginationRange.map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pageNum === params.page ? "contained" : "outlined"}
                  size="small"
                  sx={{
                    minWidth: "35px",
                    border: "1px solid #89652A",
                    backgroundColor:
                      pageNum === params.page ? "#89652A" : "white",
                    color: pageNum === params.page ? "white" : "#89652A",
                    "&:hover": {
                      backgroundColor:
                        pageNum === params.page ? "#89652A" : "#f5f5f5",
                    },
                  }}
                  onClick={() =>
                    setParams((prev) => ({ ...prev, page: pageNum }))
                  }
                  disabled={loading}
                >
                  {pageNum + 1}
                </Button>
              ))}
              {totalPages > maxVisibleButtons &&
                params.page < totalPages - maxVisibleButtons && (
                  <Typography sx={{ mx: 1 }}>...</Typography>
                )}
              <Button
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#89652A",
                  "&:hover": { backgroundColor: "#B69D74" },
                  minWidth: "10px",
                }}
                onClick={() =>
                  setParams((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                disabled={params.page >= totalPages - 1 || loading}
              >
                <ChevronRight />
              </Button>
            </Box>
            <Footer />
          </Box>
        </Box>
      </Box>
    </>
  );
};

const styles = {
  mainBox: { bgcolor: "#FAFAFA" },
  headerBox: { py: { xs: 2, md: 3 }, px: { xs: 2, md: 6.4 } },
  gridContainer: { alignItems: "center" },
  welcomeText: {
    fontWeight: 700,
    fontSize: { xs: "28px", sm: "42px", md: "42px" },
    lineHeight: 1.2,
  },
  welcomeHighlight: {
    color: "#89652A",
    fontSize: { xs: "28px", sm: "42px", md: "42px" },
    fontWeight: 700,
  },
  subTitle: {
    fontWeight: 600,
    color: "#333",
    fontSize: { xs: "20px", sm: "28px", md: "28px" },
    mt: 0.5,
    mb: 1.5,
  },
  searchBox: { position: "relative", my: { xs: 1.5, md: 2 } },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "50px",
      height: "50px",
      backgroundColor: "white",
      // boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      "& fieldset": { borderColor: "#E0E0E0" },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#89652A",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#89652A",
      },
    },
  },
  searchButton: {
    position: "absolute",
    right: 1,
    fontSize: "16px",
    top: "50%",
    color: "white",
    transform: "translateY(-50%)",
    borderRadius: "50px",
    backgroundColor: "#89652A",
    textTransform: "none",
    px: { xs: 3, md: 5 },
    py: { xs: 1.4, md: 1.4 },
    boxShadow: "0 2px 8px rgba(137, 101, 42, 0.3)",
    "&:hover": { 
      backgroundColor: "#89652A",
      boxShadow: "0 4px 12px rgba(137, 101, 42, 0.4)"
    },
  },
  imageContainer: {
    justifyContent: "center",
    alignItems: "center",
    pt: { xs: 2, md: 0 },
  },
  image: { maxWidth: "100%", height: "auto" },
  contractorCard: {
    maxWidth: "1000px",
    display: "block",
    margin: "auto",
    borderRadius: "10px",
    bgcolor: "#F5F5F5",
    p: { xs: 1.5, sm: 1, md: 1.5 },
    mb: { xs: 1, md: 1.5 },
    px: { xs: 1.5, sm: 3, md: 4, lg: 6, xl: 7 },
    cursor: "pointer"
  },
};

export default ContractorSearch;
